import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchTenantInfo } from '../../utils/tenantDetection';
import { clearSuspensionStatus, getSuspensionStatus } from '../../utils/axiosInterceptor';
import { API_URL } from '../../config/api';

// Get suspension modal callback to close modal on reactivation
let suspensionModalCallback = null;
export const setSuspensionModalCallbackForTenant = (callback) => {
  suspensionModalCallback = callback;
};

export const loadTenant = createAsyncThunk(
  'tenant/loadTenant',
  async (_, { rejectWithValue }) => {
    try {
      const tenant = await fetchTenantInfo();
      if (tenant) {
        // Store in localStorage for quick access
        localStorage.setItem('tenant', JSON.stringify(tenant));
        
        // Check if tenant is active - clear suspension status if active
        if (tenant.status === 'active') {
          const hadSuspension = getSuspensionStatus();
          clearSuspensionStatus();
          
          // Close modal if it was open
          if (hadSuspension && suspensionModalCallback) {
            suspensionModalCallback({
              isOpen: false,
              status: null,
              message: '',
              endDate: null
            });
          }
        }
        
        // Fetch modules for tenant
        try {
          const modulesResponse = await axios.get(`${API_URL}/api/public/tenant/modules`);
          const modules = modulesResponse.data.modules || [];
          localStorage.setItem('tenantModules', JSON.stringify(modules));
          return { tenant, modules };
        } catch (moduleError) {
          console.error('Error fetching modules:', moduleError);
          return { tenant, modules: [] };
        }
      }
      return { tenant: null, modules: [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const tenantSlice = createSlice({
  name: 'tenant',
  initialState: {
    tenant: null,
    modules: [],
    loading: false,
    error: null
  },
  reducers: {
    setTenant: (state, action) => {
      state.tenant = action.payload;
      if (action.payload) {
        localStorage.setItem('tenant', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('tenant');
      }
    },
    setModules: (state, action) => {
      state.modules = action.payload;
      localStorage.setItem('tenantModules', JSON.stringify(action.payload));
    },
    clearTenant: (state) => {
      state.tenant = null;
      state.modules = [];
      localStorage.removeItem('tenant');
      localStorage.removeItem('tenantModules');
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTenant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.tenant = action.payload?.tenant || null;
        state.modules = action.payload?.modules || [];
      })
      .addCase(loadTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setTenant, setModules, clearTenant } = tenantSlice.actions;

// Selector to check if a module is enabled
export const selectModuleEnabled = (moduleCode) => (state) => {
  const module = state.tenant.modules.find(m => m.code === moduleCode);
  return module?.enabled || false;
};

// Selector to get all enabled modules
export const selectEnabledModules = (state) => {
  return state.tenant.modules.filter(m => m.enabled);
};

export default tenantSlice.reducer;

